import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/authService';
import { criteriaIsComplete } from './criteriaUtils';

const DEFAULT_DEBOUNCE_MS = 1500;

function errorText(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

/**
 * Live distinct-profile count for a criteria tree.
 *
 * Counting runs on Databricks, so it is debounced: clicking several values in a row
 * costs one scan rather than one each. `groupCounts` comes back from that request,
 * keyed by the backend's dot-joined group path. Nested groups and sampling use a
 * breakdown scan; a single root at 100% reuses the total.
 *
 * `status` is one of idle | running | fresh | stale | error. A count that finishes after
 * the criteria have moved on lands as `stale`, so the number on screen is never passed
 * off as describing something it did not measure.
 */
export function useCriteriaCount(criteria, organizationId, { auto = true, debounceMs = DEFAULT_DEBOUNCE_MS } = {}) {
  const [count, setCount] = useState(null);
  const [groupCounts, setGroupCounts] = useState(null);
  const [status, setStatus] = useState('idle');
  const [tookMs, setTookMs] = useState(null);
  const [error, setError] = useState('');

  const reqId = useRef(0);
  const countedFor = useRef('');
  const latestJson = useRef('');
  const criteriaRef = useRef(criteria);
  criteriaRef.current = criteria;

  const criteriaJson = useMemo(() => (criteria ? JSON.stringify(criteria) : ''), [criteria]);
  latestJson.current = criteriaJson;
  const complete = Boolean(criteria) && criteriaIsComplete(criteria);
  const canCount = complete && Boolean(organizationId);

  const run = useCallback(async () => {
    const tree = criteriaRef.current;
    if (!organizationId || !tree || !criteriaIsComplete(tree)) return;
    const asked = JSON.stringify(tree);
    const req = ++reqId.current;
    setStatus('running');
    setError('');
    try {
      const res = await api.post('/api/v2/segments/run-count', {
        criteria: tree,
        organization_id: parseInt(organizationId, 10),
        include_group_counts: true,
      });
      if (req !== reqId.current) return;
      const n = Number(res.data?.count);
      setCount(Number.isFinite(n) ? n : null);
      setTookMs(Number(res.data?.took_ms) || null);
      const gc = res.data?.group_counts;
      setGroupCounts(gc && typeof gc === 'object' && Object.keys(gc).length ? gc : null);
      countedFor.current = asked;
      setStatus(latestJson.current === asked ? 'fresh' : 'stale');
    } catch (e) {
      if (req !== reqId.current) return;
      setStatus('error');
      setError(errorText(e?.response?.data?.detail) || 'Count failed.');
    }
  }, [organizationId]);

  // The number on screen describes the criteria it was asked about, not these.
  useEffect(() => {
    if (criteriaJson && criteriaJson === countedFor.current) return;
    setStatus((s) => (s === 'running' ? s : count == null ? 'idle' : 'stale'));
  }, [criteriaJson, count]);

  useEffect(() => {
    if (!auto || !canCount) return undefined;
    if (criteriaJson === countedFor.current) return undefined;
    const t = setTimeout(() => run(), debounceMs);
    return () => clearTimeout(t);
  }, [criteriaJson, auto, canCount, debounceMs, run]);

  return { count, groupCounts, status, tookMs, error, complete, canCount, run };
}
